import React, { useState } from "react";
import _ from "lodash";
import {
  AnalyticsCard,
  AnalyticsTable,
  AssetNameCell,
  AssetTrendCell,
  Cell,
  ColumnDef,
  DropdownFilter,
  LeadsRadialPieChart,
  metricsToRadialSlices,
  TABLE_MIN_HEIGHT,
  TableFooter,
} from "../../../common";
import {
  useLeadAssetInteractionsChartQuery,
  useLeadAssetInteractionsQuery,
} from "src/query/experiences/leadsAndReports/leads";
import { convertDatesToTimezone, dateKey } from "src/services/experiences/leadsAndReports/dateUtils";
import { formatDurationWithLabel } from "../../../utils/format";
import { ArrangeOption } from "../../../common/DropdownFilter";
import { ChartView, ReportDateFilter } from "src/types/experiences/leadsAndReports";
import styles from "../../../common/common.module.scss";

interface AssetInteractionsProps {
  storyboardId: string;
  profileId: string;
  dateFilter: ReportDateFilter | null;
}

const COLUMNS: ColumnDef[] = [
  { key: "asset", header: "Asset Name" },
  { key: "views", header: "Views", align: "right" },
  { key: "time", header: "Time Spent", align: "right" },
  { key: "completed", header: "% Completed", align: "right" },
  { key: "downloads", header: "Downloads", align: "right" },
  { key: "trends", header: "Trends", align: "right-fill" },
];

const PIE_OPTIONS = [
  { label: "Views", value: "views" },
  { label: "Time", value: "timeSpent" },
];

const getField = (mapping: any, key: string) => {
  const fields = _.get(mapping, "fields") || [];
  return _.get(
    fields.find((f: any) => f.name === key),
    "content",
    "",
  );
};


const AssetRow: React.FC<{
  row: any;
  cacheKey: string;
  dateRequest: Record<string, unknown>;
}> = ({ row, cacheKey, dateRequest }) => {
  return (
    <>
      <AssetNameCell asset={_.get(row, "asset")} />
      <Cell headline={row.views} />
      <Cell headline={formatDurationWithLabel(row.timeSpent).value} />
      <Cell headline={`${Math.round(row.percentageCompleted)}%`} />
      <Cell headline={row.downloads} />
      <AssetTrendCell row={row} cacheKey={cacheKey} extraRequest={dateRequest} />
    </>
  );
};

export const AssetInteractions: React.FC<AssetInteractionsProps> = ({
  storyboardId,
  profileId,
  dateFilter,
}) => {
  const [page, setPage] = useState(0);
  const [chart, setChart] = useState<ChartView>("table_view");
  const [viewOption, setViewOption] = useState<ArrangeOption>({
    label: "Views",
    value: "views",
    name: "name",
  });
  const dateRequest = convertDatesToTimezone(dateFilter ?? undefined);
  const request = { storyboard: storyboardId, ...dateRequest };
  const key = dateKey(dateFilter ?? undefined);
  const { data, isLoading } = useLeadAssetInteractionsQuery(
    profileId,
    request,
    key,
    page,
    Boolean(dateFilter),
  );
  const rows = data?.records ?? [];

  const { data: chartData, isLoading: isChartLoading } = useLeadAssetInteractionsChartQuery(
    profileId,
    request,
    key,
    Boolean(dateFilter) && chart === "pie_chart_view",
  );
  const pieData = chartData?.records ?? [];

  const renderChart = () => {
    switch (chart) {
      case "pie_chart_view":
        return (
          <div className={styles.chartFull}>
            <LeadsRadialPieChart
              data={metricsToRadialSlices(
                pieData.map((row: any) => ({
                  name: getField(_.get(row, "asset"), "name"),
                  views: row.views,
                  timeSpent: row.timeSpent,
                  percentageCompleted: row.percentageCompleted,
                  downloads: row.downloads,
                })),
                viewOption,
              )}
              measureLabel={viewOption.label}
              height={280}
              loading={isChartLoading}
            />
          </div>
        );
      case "table_view":
        return (
          <div className={styles.assetAnalyticsTable}>
            <AnalyticsTable
              columns={COLUMNS}
              rows={rows}
              loading={isLoading}
              gridTemplate="minmax(200px, 2fr) repeat(5, 1fr)"
              renderRow={(row: any) => (
                <AssetRow
                  row={row}
                  cacheKey={key}
                  dateRequest={{ startDate: dateRequest.startDate, endDate: dateRequest.endDate }}
                />
              )}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <AnalyticsCard
      title="Asset Interactions"
      count={data?.totalRecordsCount ?? rows.length}
      currentChart={chart}
      disableView={["graph_view"]}
      onChartChange={setChart}
      minHeight={TABLE_MIN_HEIGHT.full}
      action={
        chart === "pie_chart_view" ? (
          <DropdownFilter
            arrangeOption={viewOption}
            handleArrange={(o) => setViewOption((s) => ({ ...s, ...o }))}
            disableOption={[]}
            options={PIE_OPTIONS}
          />
        ) : undefined
      }
      footer={
        <TableFooter
          currentChart={chart}
          handlePageChange={setPage}
          pagination={data}
          paginationName="Assets"
        />
      }
    >
      {renderChart()}
    </AnalyticsCard>
  );
};
